import React, { useState } from 'react';
import { Mail, MapPin } from 'lucide-react';
import contacthero from '../assets/contacthero.png';
import { sendContactMessage } from '../api/contact';
import { ToastContainer, useToast } from '../components/common/Toast';

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('idle');
  const { toast, showToast, dismissToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await sendContactMessage({
        name: name.trim(),
        email: email.trim(),
        description: description.trim(),
        source: 'contact',
      });
      setName('');
      setEmail('');
      setDescription('');
      setStatus('idle');
      showToast('Thanks—your message was sent. We will get back to you when we can.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not send message.', 'error');
      setStatus('idle');
    }
  };

  return (
    <>
    <main>
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="contact-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${contacthero})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />

        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-xl text-center">
            <h1
              id="contact-hero-heading"
              className="text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Contact MedIntel
            </h1>
            <p className="mt-4 text-pretty text-base leading-relaxed text-slate-100/95 sm:text-lg">
              Story ideas, corrections, and collaboration—we read every message and respond when we can. MedIntel
              does not provide medical advice; for clinical questions, please speak with your healthcare team.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 sm:py-20 lg:py-24" aria-labelledby="contact-form-heading">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid min-h-0 gap-12 lg:grid-cols-2 lg:gap-14 lg:items-stretch">
            <div className="flex min-h-0 flex-col lg:h-full">
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Reach us</p>
              <h2
                id="contact-form-heading"
                className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                Editorial & general inquiries
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Use the form for pitches, feedback on our coverage, or technical issues with the site. For sensitive
                corrections, you can also email us directly.
              </p>
              <div className="mt-8 flex min-h-0 flex-col lg:h-full lg:flex-1">
                <div className="flex min-h-0 flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm ring-1 ring-slate-200/60 lg:h-full lg:flex-1">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" aria-hidden />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Email</p>
                        <a
                          href="mailto:info@MedIntel.com"
                          className="mt-1 inline-block text-slate-700 no-underline underline-offset-4 visited:text-slate-700 hover:text-brand-600 hover:underline"
                        >
                          info@MedIntel.com
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" aria-hidden />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Address</p>
                        <p className="mt-1 leading-relaxed text-slate-600">
                          Mumbai, Maharashtra, India
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto border-t border-slate-100 pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Response time</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      We typically reply within a few business days.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex w-full flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8 lg:h-full lg:min-h-0"
              aria-busy={status === 'loading'}
            >
              <div className="flex min-h-0 flex-col space-y-5 lg:flex-1">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-slate-900">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(ev) => setName(ev.target.value)}
                    disabled={status === 'loading'}
                    className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-slate-900">
                    Email id
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    disabled={status === 'loading'}
                    className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="contact-description" className="block text-sm font-medium text-slate-900">
                    Description
                  </label>
                  <textarea
                    id="contact-description"
                    name="description"
                    rows={5}
                    required
                    value={description}
                    onChange={(ev) => setDescription(ev.target.value)}
                    disabled={status === 'loading'}
                    className="mt-1.5 block w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                    placeholder="How can we help?"
                  />
                </div>
              </div>
              <div className="mt-auto flex justify-center border-t border-slate-100 pt-6">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex w-full items-center justify-center rounded-md bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-brand-900/15 transition hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === 'loading' ? 'Sending…' : 'Send message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-16 sm:py-20 lg:py-24" aria-labelledby="contact-map-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Location</p>
            <h2
              id="contact-map-heading"
              className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
            >
              Find us on the map
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">Mumbai, Maharashtra, India</p>
          </div>

          <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-100 shadow-lg ring-1 ring-slate-200/60">
            <div className="relative aspect-[4/3] w-full sm:aspect-video">
              <iframe
                title="Map showing MedIntel office location"
                src="https://maps.google.com/maps?q=Mumbai%2C%20Maharashtra%2C%20India&z=14&output=embed"
                className="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          <p className="mt-4 text-center text-sm text-slate-600">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Mumbai%2C%20Maharashtra%2C%20India"
              className="font-medium text-slate-700 no-underline underline-offset-4 visited:text-slate-700 hover:text-brand-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
            <span className="text-slate-400" aria-hidden>
              {' '}
              ·
            </span>
            <span className="text-slate-500"> Opens in a new tab</span>
          </p>
        </div>
      </section>
    </main>
    <ToastContainer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

export default Contact;
